import CloseButton from '@components/CloseButton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  LoadingDialog,
} from '@components/DewiDialog'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@components/Furry/Button'
import { PublicKey } from '@solana/web3.js'
import useStaking from '@hooks/useStaking'
import LoadingCircle from '@icons/loading-circle'
import { Box } from '@mui/material'

const UnstakeDialog = ({
  nft,
  open,
  onCloseHeader,
}: {
  nft: PublicKey
  open: boolean
  onCloseHeader: () => void
}) => {
  const { t } = useTranslation()

  const {
    isStaked,
    readyToUnstake,
    initializeUnstake,
    initializedUnstake,
    unstake,
    stake,
    stakeMetadataLoading,
    timeLeftForCooldwon,
  } = useStaking({
    nft,
  })

  const handleStake = useCallback(() => {
    if (readyToUnstake) {
      unstake(nft)
    } else if (isStaked && !initializedUnstake) {
      initializeUnstake(nft)
    } else if (!isStaked) {
      stake(nft)
    }
  }, [
    readyToUnstake,
    isStaked,
    initializedUnstake,
    unstake,
    nft,
    initializeUnstake,
    stake,
  ])

  const isCoolingDown = useMemo(() => {
    const cooldownResponse = timeLeftForCooldwon()
    if (!cooldownResponse) return false
    return cooldownResponse.total > 0
  }, [timeLeftForCooldwon])

  const Bodycontent = useMemo(() => {
    if (readyToUnstake)
      return t('holdings.holdingCard.unstakeDialog.messageReadyToUnstake')
    return t('holdings.holdingCard.unstakeDialog.message')
  }, [readyToUnstake, t])

  const ButtonContent = useMemo(() => {
    if (stakeMetadataLoading) {
      return <LoadingCircle />
    }
    const cooldownResponse = timeLeftForCooldwon()
    if (isCoolingDown && cooldownResponse)
      return t('holdings.holdingCard.cooldown', {
        days: cooldownResponse.days,
        hours: cooldownResponse.hours,
        minutes: cooldownResponse.minutes,
        seconds: cooldownResponse.seconds,
      })
    if (readyToUnstake) return t('holdings.holdingCard.unstake')
    if (!isStaked) return t('holdings.holdingCard.stake')
    return t('holdings.holdingCard.initializedUnstake')
  }, [
    stakeMetadataLoading,
    readyToUnstake,
    t,
    isStaked,
    isCoolingDown,
    timeLeftForCooldwon,
  ])

  return (
    <Dialog open={open} onClose={() => {}} onExited={() => {}}>
      <>
        <DialogHeader>
          <Box alignSelf="flex-end">
            <CloseButton onClick={onCloseHeader} />
          </Box>
        </DialogHeader>
        <DialogContent>
          <span className="text-lg mb-4 px-4">{Bodycontent}</span>
          <Button
            ripple
            fullWidth
            onClick={handleStake}
            className={`justify-center flex ${isCoolingDown ? 'pointer-events-none' : ''}`}
            disabled={isCoolingDown}
          >
            {ButtonContent}
          </Button>
        </DialogContent>
      </>
    </Dialog>
  )
}

export default UnstakeDialog
